import {useMutation, useQueryClient} from '@github-ui/react-query'
import type {CommandType} from '@github-ui/copilot-shared-ui/ahp/command-types'
import type {Command, CommandsResponse} from '../types/session'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {uuid} from '../utils/uuid'
import {SessionContext} from '../contexts/SessionContext'
import {use} from 'react'
import {generateSessionCommandsQueryKey} from './useListCommands'

interface CreateCommandParams {
  sessionId: string
  content: string
  /** Command type for CLI protocol routing. Defaults to 'user_message'. */
  type?: CommandType
  /** Opaque snapshot for optimistic-update rollback. Forwarded to onError; never sent to server. */
  snapshot?: unknown
}

interface CreateCommandMutationParams extends CreateCommandParams {
  optimisticId: string
}

interface MutationContext {
  queryKey: string[]
  optimisticId: string
  snapshot?: unknown
}

interface UseCreateCommandOptions {
  onError?: (error: Error, snapshot?: unknown) => void
  onSuccess?: (command: Command, snapshot?: unknown) => void
}

/**
 * Hook for creating session commands and immediately showing them in the UI before server confirmation.
 */
export function useCreateCommand(options?: UseCreateCommandOptions) {
  const queryClient = useQueryClient()
  const copilotApiUrl = use(SessionContext)?.copilotApiUrl

  const createCommandMutation = useMutation<Command, Error, CreateCommandMutationParams, MutationContext>({
    mutationKey: ['createCommand'],
    onMutate: async (params: CreateCommandMutationParams) => {
      const {sessionId, content, type, optimisticId, snapshot} = params
      const queryKey = generateSessionCommandsQueryKey(sessionId, copilotApiUrl)

      // Cancel any existing refetches to prevent race conditions
      await queryClient.cancelQueries({queryKey})

      // Get current cache data
      const currentData = queryClient.getQueryData<CommandsResponse>(queryKey)

      // Create optimistic command
      const optimisticCommand: Command = {
        id: optimisticId,
        external_id: null,
        user_id: -1,
        session_id: sessionId,
        content,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        state: 'queued',
        type: type ?? 'user_message',
        error: null,
      }

      // Add optimistic command to cache
      const updatedData: CommandsResponse = {
        commands: [...(currentData?.commands ?? []), optimisticCommand],
      }
      queryClient.setQueryData(queryKey, updatedData)

      // Return context for cleanup on error or success
      return {queryKey, optimisticId, snapshot}
    },
    mutationFn: async (params: CreateCommandMutationParams) => {
      const {sessionId, content, type} = params

      const response = await fetchCAPI({
        path: `/agents/sessions/${sessionId}/commands`,
        method: 'POST',
        body: {content, type: type ?? 'user_message'},
        ...(copilotApiUrl ? {copilotApiUrl} : {}),
      })

      if (!response.ok) {
        throw new Error('Failed to submit command')
      }

      const command = (await response.json()) as Command

      // Default to queued state if missing to prevent UI errors
      if (!command.state) {
        command.state = 'queued'
      }

      return command
    },
    onError: (error, variables, context) => {
      if (!context?.queryKey || !context?.optimisticId) {
        options?.onError?.(error, context?.snapshot)
        return
      }

      // Remove only this specific optimistic command that failed
      // Don't rollback to previousData as that would affect other in-flight mutations
      queryClient.setQueryData<CommandsResponse>(context.queryKey, currentData => {
        if (!currentData) return currentData

        // Remove only the optimistic command that failed
        const updatedCommands = currentData.commands.filter(cmd => cmd.id !== context.optimisticId)

        return {
          commands: updatedCommands,
        }
      })

      // Call the error handler
      options?.onError?.(error, context.snapshot)
    },
    onSuccess: (data, variables, context) => {
      if (!context?.queryKey) return

      // Replace the optimistic command with the real one from the server to prevent UI flashing
      queryClient.setQueryData<CommandsResponse>(context.queryKey, currentData => {
        if (!currentData) return currentData

        const updatedCommands = currentData.commands.map(cmd =>
          cmd.id === context.optimisticId
            ? {
                ...data,
                content: data.content ?? cmd.content, // Preserve the original content in case it's not returned
                type: data.type ?? cmd.type, // Preserve the command type for prompt response filtering
              }
            : cmd,
        )

        return {
          commands: updatedCommands,
        }
      })

      // Trigger a refetch in the background to ensure we're in sync
      queryClient.invalidateQueries({queryKey: context.queryKey})

      // Call success handler
      options?.onSuccess?.(data, context.snapshot)
    },
  })

  const createCommand = (params: CreateCommandParams) => {
    const optimisticId = `optimistic-${uuid()}`
    createCommandMutation.mutate({...params, optimisticId})
  }

  return {
    createCommand,
    isPending: createCommandMutation.isPending,
  }
}
